export type ApiErrorKind = "network" | "auth" | "forbidden" | "server" | "client" | "unknown";

export interface ApiError {
    kind: ApiErrorKind;
    message: string;
    status?: number;
    details?: unknown;
}

interface ErrorLike {
    message?: string;
    request?: unknown;
    response?: {
        status?: number;
        statusText?: string;
        data?: unknown;
    };
}

interface ApiResponseLike {
    success?: boolean;
    status?: number;
    statusCode?: number;
    message?: unknown;
    error?: unknown;
}

const defaultMessages: Record<ApiErrorKind, string> = {
    network: "Unable to reach the server. Check your connection and try again.",
    auth: "Your session has expired. Please sign in again.",
    forbidden: "You do not have permission to view this data.",
    server: "The server could not complete the request. Please try again.",
    client: "The request could not be completed. Please check the details and try again.",
    unknown: "Something went wrong. Please try again.",
};

export const isApiError = (error: unknown): error is ApiError => {
    if (!error || typeof error !== "object") return false;

    const candidate = error as Partial<ApiError>;
    return typeof candidate.kind === "string" && typeof candidate.message === "string";
};

const getErrorLike = (error: unknown): ErrorLike => {
    if (!error || typeof error !== "object") return {};
    return error as ErrorLike;
};

const getMessageFromData = (data: unknown): string | undefined => {
    if (typeof data === "string" && data.trim()) return data;
    if (!data || typeof data !== "object") return undefined;

    const candidate = data as { message?: unknown; error?: unknown };
    if (typeof candidate.message === "string" && candidate.message.trim()) return candidate.message;
    if (typeof candidate.error === "string" && candidate.error.trim()) return candidate.error;

    return undefined;
};

const getMessage = (source: ErrorLike, kind: ApiErrorKind): string => {
    return getMessageFromData(source.response?.data) || source.response?.statusText || source.message || defaultMessages[kind];
};

const getKindFromStatus = (status?: number): ApiErrorKind => {
    if (status === 401) return "auth";
    if (status === 403) return "forbidden";
    if (typeof status === "number" && status >= 500) return "server";
    if (typeof status === "number") return "client";
    return "server";
};

const toPublicApiError = (error: ApiError): ApiError => ({
    kind: error.kind,
    message: error.message,
    status: error.status,
    details: error.details,
});

export const getApiErrorFromResponseBody = (body: unknown, fallbackMessage: string): ApiError => {
    const responseBody = body && typeof body === "object" ? (body as ApiResponseLike) : {};
    const status =
        typeof responseBody.status === "number"
            ? responseBody.status
            : typeof responseBody.statusCode === "number"
              ? responseBody.statusCode
              : undefined;
    const kind = getKindFromStatus(status);

    return {
        kind,
        status,
        message: getMessageFromData(body) || fallbackMessage || defaultMessages[kind],
        details: body,
    };
};

export function throwIfApiResponseFailed<T extends { success?: boolean }>(
    body: T,
    fallbackMessage: string,
): asserts body is T & { success: true } {
    if (body?.success === false) {
        throw getApiErrorFromResponseBody(body, fallbackMessage);
    }
}

export const normalizeApiError = (error: unknown): ApiError => {
    if (isApiError(error)) return toPublicApiError(error);

    const source = getErrorLike(error);
    const status = source.response?.status;

    if (typeof status === "number") {
        if (status === 401) {
            return {
                kind: "auth",
                status,
                message: getMessage(source, "auth"),
                details: source.response?.data,
            };
        }

        if (status === 403) {
            return {
                kind: "forbidden",
                status,
                message: getMessage(source, "forbidden"),
                details: source.response?.data,
            };
        }

        if (status >= 500) {
            return {
                kind: "server",
                status,
                message: getMessage(source, "server"),
                details: source.response?.data,
            };
        }

        return {
            kind: "client",
            status,
            message: getMessage(source, "client"),
            details: source.response?.data,
        };
    }

    if (source.request || source.message === "Network Error") {
        return {
            kind: "network",
            message: getMessage(source, "network"),
        };
    }

    return {
        kind: "unknown",
        message: source.message || defaultMessages.unknown,
    };
};

export const getApiErrorLogMetadata = (error: unknown): Pick<ApiError, "kind" | "status" | "message"> => {
    const { kind, status, message } = normalizeApiError(error);
    return { kind, status, message };
};
